import { createContext, ReactNode, useMemo } from 'react';
import useRouteTransition from '../hooks/useRouteTransition';

// Lives above the IonRouterOutlet so the transition state survives the per-page
// remount, and drives the single persistent RouteTransitionOverlay. Pages:
//  - read isTransitioning / isOverlayRequested (the overlay reads these),
//  - report navigation loading via requestBusy/releaseBusy (keeps a transition
//    session open until the destination is ready, incl. a redirect target),
//  - force the overlay on without a navigation via requestOverlay/releaseOverlay
//    (form / FormInit loading).
interface RouteTransitionContextType {
  isProvided: boolean;
  isTransitioning: boolean;
  isOverlayRequested: boolean;
  requestBusy: () => void;
  releaseBusy: () => void;
  requestOverlay: () => void;
  releaseOverlay: () => void;
}

const defaultValues: RouteTransitionContextType = {
  isProvided: false,
  isTransitioning: false,
  isOverlayRequested: false,
  requestBusy: () => undefined,
  releaseBusy: () => undefined,
  requestOverlay: () => undefined,
  releaseOverlay: () => undefined,
};

export const RouteTransitionContext =
  createContext<RouteTransitionContextType>(defaultValues);

const RouteTransitionContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const {
    isTransitioning,
    isOverlayRequested,
    requestBusy,
    releaseBusy,
    requestOverlay,
    releaseOverlay,
  } = useRouteTransition();

  const value = useMemo(
    () => ({
      isProvided: true,
      isTransitioning,
      isOverlayRequested,
      requestBusy,
      releaseBusy,
      requestOverlay,
      releaseOverlay,
    }),
    [
      isTransitioning,
      isOverlayRequested,
      requestBusy,
      releaseBusy,
      requestOverlay,
      releaseOverlay,
    ]
  );

  return (
    <RouteTransitionContext.Provider value={value}>
      {children}
    </RouteTransitionContext.Provider>
  );
};

export default RouteTransitionContextProvider;
